import React from "react";
import { useSelector } from "react-redux";
import { generatePath, useNavigate } from "react-router-dom";
import { path } from "utils/const";

const formatAmount = (amount) => {
    if (!amount) return "0";

    const num = parseFloat(amount);
    if (num >= 10000000) {
        return `${(num / 10000000).toFixed(2)} Cr`;
    }
    if (num >= 100000) {
        return `${(num / 100000).toFixed(2)} L`;
    }
    if (num >= 1000) {
        return `${(num / 1000).toFixed(2)} K`;
    }
    return num.toLocaleString("en-IN", { maximumFractionDigits: 2 });
};

const DueRangeItem = ({ label, amount, color }) => {
    const formattedAmount = formatAmount(amount);

    return (
        <div
            style={{
                width: "100%",
                height: "17px",
                justifyContent: "space-between",
                opacity: 1,
                display: "flex",
                alignItems: "center",
                marginBottom: "8.02px",
            }}
        >
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                }}
            >
                <div
                    style={{
                        width: "8px",
                        height: "8px",
                        borderRadius: "50%",
                        backgroundColor: color,
                        marginRight: "8px",
                    }}
                />
                <span
                    style={{
                        fontFamily: "Manrope",
                        fontWeight: 600,
                        fontStyle: "SemiBold",
                        fontSize: "12.03px",
                        lineHeight: "100%",
                        letterSpacing: "-1%",
                        color: "#646464",
                    }}
                >
                    {label}
                </span>
            </div>
            <span
                style={{
                    fontFamily: "Manrope",
                    fontWeight: 600,
                    fontStyle: "SemiBold",
                    fontSize: "12.54px",
                    lineHeight: "100%",
                    letterSpacing: "0px",
                    color: "#000000",
                }}
            >
                ₹ {formattedAmount}
            </span>
        </div>
    );
};

const ViewKhata = ({ onViewKhataClick }) => {
    return (
        <div
            onClick={onViewKhataClick}
            style={{
                width: "100%",
                height: "41.12px",
                borderRadius: "8.02px",
                opacity: 1,
                gap: "8.73px",
                padding: "12.03px 8.73px",
                background: "#E6EDED",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                marginTop: "10px",
            }}
        >
            <span
                style={{
                    fontWeight: 600,
                    fontSize: "14.04px",
                    lineHeight: "100%",
                    letterSpacing: "0px",
                    color: "#008080",
                }}
            >
                View Khata &gt;
            </span>
        </div>
    );
};

const HowOldAreYourDues = ({ creditDetails, onViewKhataClick }) => {
    const navigate = useNavigate();
    const { me } = useSelector((state) => state.app);

    const handleViewKhataClick = () => {
        if (onViewKhataClick) {
            onViewKhataClick();
        } else if (me?.id) {
            // Navigate to My Transactions page (mybuys)
            navigate(generatePath(path.mybuys, { id: me.id }));
        }
    };

    if (!creditDetails) return null;

    // Extract data from creditDetails using the correct slabs
    const freshDues = creditDetails.overdue_in_0_to_10_days_amount || 0;
    const elevenToFifteenDues = creditDetails.overdue_in_11_to_15_days_amount || 0;
    const sixteenToThirtyDues = creditDetails.overdue_in_16_to_30_days_amount || 0;
    const thirtyPlusDues = creditDetails.overdue_in_30_plus_days_amount || 0;

    // Calculate total dues
    const totalDues = freshDues + elevenToFifteenDues + sixteenToThirtyDues + thirtyPlusDues;

    // Calculate dues older than 10 days
    const duesOver10Days = elevenToFifteenDues + sixteenToThirtyDues + thirtyPlusDues;

    // Calculate tip based on the rules
    let tipText = "";
    let tipAmount = 0;

    if (duesOver10Days === 0) {
        // No tip if there are no dues over 10 days
        tipText = "";
    } else if ((thirtyPlusDues / totalDues) > 0.3) {
        // If B3 / T > 30%, Pay ₹B3 to come under 30 days
        tipAmount = thirtyPlusDues;
        tipText = `Pay ₹ ${formatAmount(tipAmount)} to bring dues under 30 days`;
    } else if (((thirtyPlusDues + sixteenToThirtyDues) / totalDues) > 0.3) {
        // If (B3 + B2) / T > 30%, Pay ₹(B3 + B2) to come under 15 days
        tipAmount = thirtyPlusDues + sixteenToThirtyDues;
        tipText = `Pay ₹ ${formatAmount(tipAmount)} to bring dues under 15 days`;
    } else if ((thirtyPlusDues + sixteenToThirtyDues + elevenToFifteenDues) > 0) {
        // If (B3 + B2 + B1) > 0, Pay ₹(B3 + B2 + B1) to come under 10 days
        tipAmount = thirtyPlusDues + sixteenToThirtyDues + elevenToFifteenDues;
        tipText = `Pay ₹ ${formatAmount(tipAmount)} to bring dues under 10 days`;
    }

    return (
        <div
            style={{
                width: "100%",
                borderRadius: "10px",
                opacity: 1,
                gap: "14px",
                borderWidth: "1px",
                padding: "14.04px",
                paddingBottom: "16px",
                background: "#FFFFFF",
                border: "1px solid #EFEFEF",
                marginTop: "16px",
            }}
        >
            <div
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "14px",
                }}
            >
                <span
                    style={{
                        fontFamily: "Manrope",
                        fontWeight: 500,
                        fontStyle: "Medium",
                        fontSize: "14px",
                        lineHeight: "100%",
                        letterSpacing: "0px",
                        color: "#000000",
                    }}
                >
                    How old are your dues?
                </span>
                <span
                    style={{
                        fontFamily: "Manrope",
                        fontWeight: 500,
                        fontStyle: "Medium",
                        fontSize: "19.96px",
                        lineHeight: "100%",
                        letterSpacing: "0px",
                        color: "#000000",
                    }}
                >
                    💡
                </span>
            </div>

            {tipText && (
                <>
                    <div
                        style={{
                            width: "100%",
                            height: "24px",
                            borderRadius: "13.02px",
                            opacity: 1,
                            gap: "6.71px",
                            borderWidth: "0.98px",
                            padding: "4px 8.94px",
                            background: "#EEFBFA",
                            border: "0.98px solid #098D8366",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            marginBottom: "14px",
                        }}
                    >
                        <span
                            style={{
                                fontFamily: "Manrope",
                                fontWeight: 500,
                                fontSize: "12px",
                                lineHeight: "100%",
                                letterSpacing: "-1%",
                                color: "#098D83",
                            }}
                        >
                            {tipText}
                        </span>
                    </div>
                    <div
                        style={{
                            width: "100%",
                            borderTop: "1px dashed #D9D9D9",
                            marginBottom: "14px",
                        }}
                    />
                </>
            )}

            <div
                style={{
                    width: "100%",
                    opacity: 1,
                    gap: "8.02px",
                }}
            >
                <DueRangeItem label="30+ Days" amount={thirtyPlusDues} color="#EF4444" />
                <DueRangeItem label="16-30 Days" amount={sixteenToThirtyDues} color="#F97316" />
                <DueRangeItem label="11-15 Days" amount={elevenToFifteenDues} color="#F59E0B" />
                <DueRangeItem label="Fresh (<10 Days)" amount={freshDues} color="#22C55E" />

                <div
                    style={{
                        width: "100%",
                        height: "40px",
                        display: "flex",
                        alignItems: "center",
                        marginTop: "10px",
                        position: "relative",
                    }}
                >
                    <div style={{ width: "100%", display: "flex", flexDirection: "column", gap: "6px" }}>
                        <div
                            style={{
                                width: "100%",
                                display: "flex",
                                justifyContent: "space-between",
                            }}
                        >
                            <span style={{ fontSize: "10px", color: "#666", fontFamily: "Manrope" }}>Old</span>
                            <span style={{ fontSize: "10px", color: "#666", fontFamily: "Manrope" }}>Fresh</span>
                        </div>
                        <div
                            style={{
                                width: "100%",
                                height: "6px",
                                background: (() => {
                                    // Calculate percentages based on actual dues
                                    const p1 = totalDues > 0 ? (thirtyPlusDues / totalDues) * 100 : 0;
                                    const p2 = totalDues > 0 ? (sixteenToThirtyDues / totalDues) * 100 : 0;
                                    const p3 = totalDues > 0 ? (elevenToFifteenDues / totalDues) * 100 : 0;
                                    const p4 = totalDues > 0 ? (freshDues / totalDues) * 100 : 0;

                                    const stop1 = p1;
                                    const stop2 = p1 + p2;
                                    const stop3 = p1 + p2 + p3;

                                    return `linear-gradient(90deg, #EF4444 0%, #EF4444 ${stop1}%, #F97316 ${stop1}%, #F97316 ${stop2}%, #F59E0B ${stop2}%, #F59E0B ${stop3}%, #22C55E ${stop3}%, #22C55E 100%)`;
                                })(),
                                borderRadius: "3px",
                            }}
                        />
                    </div>
                </div>

                <ViewKhata onViewKhataClick={handleViewKhataClick} />
            </div>
        </div>
    );
};

export default HowOldAreYourDues;
